import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { View, Text, Image, type StyleProp, type ViewStyle } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  type SharedValue,
} from 'react-native-reanimated';
import { StackedCarousel } from '../../components/common/stacked-carousel/StackedCarousel';
import { Clickable2 } from '../../components/common/Clickable2';
import { SvgIcon } from '../../components/common/SvgIcon';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import {
  type SubscriptionListStyles,
  SLIDE_CARD_HEIGHT,
  SLIDE_CARD_INNER_HEIGHT,
  SLIDE_IMAGE_HEIGHT,
  SLIDE_BODY_EXTRA,
  SLIDE_STACK_OFFSET,
  SLIDE_WIDTH,
  CUSTOM_SLIDE_CARD_HEIGHT,
  CUSTOM_SLIDE_CARD_INNER_HEIGHT,
  CUSTOM_SLIDE_IMAGE_HEIGHT,
  CUSTOM_SLIDE_BODY_EXTRA,
  SUBSCRIPTION_CAROUSEL_CHROME_EXTRA,
} from './subscriptionListStyles';

type SlideMetrics = {
  card_height: number;
  inner_height: number;
  image_height: number;
  body_height: number;
};

/** Expanded carousel chrome (card + paginator gap) used by merged sticky collapse. */
export const MERGED_CAROUSEL_EXPANDED_HEIGHT = SLIDE_CARD_HEIGHT + SUBSCRIPTION_CAROUSEL_CHROME_EXTRA;
/** Collapsed chrome ≈ custom-plan slider card + same paginator chrome as expanded. */
export const MERGED_CAROUSEL_COLLAPSED_HEIGHT = CUSTOM_SLIDE_CARD_HEIGHT + SUBSCRIPTION_CAROUSEL_CHROME_EXTRA;
export const MERGED_DAY_PILL_BAR_HEIGHT = 44;
/**
 * Scroll offset that keeps list top flush with collapsed chrome bottom.
 * Must equal expanded−collapsed host height — a hardcoded value mis-aligns the list.
 */
export const MERGED_CAROUSEL_COLLAPSE_DISTANCE = Math.round(
  MERGED_CAROUSEL_EXPANDED_HEIGHT - MERGED_CAROUSEL_COLLAPSED_HEIGHT,
);

function resolveSlideMetrics(compact?: boolean): SlideMetrics {
  if (compact) {
    return {
      card_height: CUSTOM_SLIDE_CARD_HEIGHT,
      inner_height: CUSTOM_SLIDE_CARD_INNER_HEIGHT,
      image_height: CUSTOM_SLIDE_IMAGE_HEIGHT,
      body_height: CUSTOM_SLIDE_BODY_EXTRA,
    };
  }
  return {
    card_height: SLIDE_CARD_HEIGHT,
    inner_height: SLIDE_CARD_INNER_HEIGHT,
    image_height: SLIDE_IMAGE_HEIGHT,
    body_height: SLIDE_BODY_EXTRA,
  };
}

const EXPANDED_METRICS = resolveSlideMetrics(false);
const COLLAPSED_METRICS = resolveSlideMetrics(true);

const CategorySlideHeroImage = React.memo(function CategorySlideHeroImage({
  uri,
  styles,
}: {
  uri: string;
  styles: SubscriptionListStyles;
}) {
  const [image_failed, set_image_failed] = useState(false);
  useEffect(() => {
    set_image_failed(false);
  }, [uri]);
  if (image_failed) {
    return <View style={styles.categorySlidePlaceholder} />;
  }
  return (
    <Image
      source={{ uri }}
      style={styles.categorySlideImage}
      resizeMode="cover"
      fadeDuration={0}
      onError={() => set_image_failed(true)}
    />
  );
});

const CategorySliderSlide = React.memo(function CategorySliderSlide({
  imageUri,
  title,
  isActive,
  onPress,
  onPressSeeMore,
  show_see_more_visual,
  visualOnly,
  styles,
  themeColors,
  metrics,
  collapseProgress,
}: {
  imageUri: string | null;
  title: string;
  isActive: boolean;
  onPress: () => void;
  onPressSeeMore?: () => void;
  show_see_more_visual?: boolean;
  visualOnly?: boolean;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  metrics: SlideMetrics;
  collapseProgress?: SharedValue<number>;
}) {
  const { t } = useTranslation();
  const { textAlign, writingDirection } = useRtl();
  const uri = imageUri && imageUri.trim() ? imageUri : undefined;

  const root_anim_style = useAnimatedStyle(() => {
    if (collapseProgress == null) {
      return { height: metrics.inner_height };
    }
    const p = collapseProgress.value;
    return {
      height: interpolate(
        p,
        [0, 1],
        [EXPANDED_METRICS.inner_height, COLLAPSED_METRICS.inner_height],
        Extrapolation.CLAMP,
      ),
    };
  }, [collapseProgress, metrics.inner_height]);

  const frame_anim_style = useAnimatedStyle(() => {
    if (collapseProgress == null) {
      return { height: metrics.inner_height };
    }
    const p = collapseProgress.value;
    return {
      height: interpolate(
        p,
        [0, 1],
        [EXPANDED_METRICS.inner_height, COLLAPSED_METRICS.inner_height],
        Extrapolation.CLAMP,
      ),
    };
  }, [collapseProgress, metrics.inner_height]);

  const image_anim_style = useAnimatedStyle(() => {
    if (collapseProgress == null) {
      return { height: metrics.image_height };
    }
    const p = collapseProgress.value;
    return {
      height: interpolate(
        p,
        [0, 1],
        [EXPANDED_METRICS.image_height, COLLAPSED_METRICS.image_height],
        Extrapolation.CLAMP,
      ),
    };
  }, [collapseProgress, metrics.image_height]);

  const body_anim_style = useAnimatedStyle(() => {
    if (collapseProgress == null) {
      return { height: metrics.body_height, opacity: 1 };
    }
    const p = collapseProgress.value;
    return {
      height: interpolate(
        p,
        [0, 1],
        [EXPANDED_METRICS.body_height, COLLAPSED_METRICS.body_height],
        Extrapolation.CLAMP,
      ),
      opacity: interpolate(p, [0, 0.35, 1], [1, 0, 0], Extrapolation.CLAMP),
    };
  }, [collapseProgress, metrics.body_height]);

  const root_style: StyleProp<ViewStyle> = [styles.categorySlideVisualRoot, { height: metrics.inner_height }];
  const frame_style: StyleProp<ViewStyle> = [
    styles.categorySlideFrame,
    isActive && styles.categorySlideFrameActive,
    { height: metrics.inner_height },
  ];
  const image_wrap_style: StyleProp<ViewStyle> = [styles.categorySlideImageWrap, { height: metrics.image_height }];

  const body = (
    <Animated.View style={[frame_style, frame_anim_style]}>
      <View style={styles.categorySlide}>
        <Animated.View style={[image_wrap_style, image_anim_style]}>
          {uri ? <CategorySlideHeroImage uri={uri} styles={styles} /> : <View style={styles.categorySlidePlaceholder} />}
        </Animated.View>
        <Animated.View style={[styles.categorySlideBody, body_anim_style]} pointerEvents="box-none">
          <Text style={[styles.categorySlideTitle, { textAlign, writingDirection }]} numberOfLines={1} ellipsizeMode="tail">
            {title}
          </Text>
          {show_see_more_visual ? (
            <View style={styles.categorySlideFooterRow} pointerEvents="none">
              <View style={styles.categorySlideSeeMoreInner}>
                <Text style={styles.categorySlideSeeMoreText}>{t('subscriptions.seeMore') || 'See more'}</Text>
                <SvgIcon name="arrow-right" size={appTheme.sizes.iconSizeSm} color={themeColors.primary} />
              </View>
            </View>
          ) : null}
          {!visualOnly && onPressSeeMore ? (
            <View style={styles.categorySlideFooterRow}>
              <Clickable2 style={styles.categorySlideSeeMore} onPress={onPressSeeMore} hitSlop={appTheme.spacing.sm} haptics>
                <View style={styles.categorySlideSeeMoreInner}>
                  <Text style={styles.categorySlideSeeMoreText}>{t('subscriptions.seeMore') || 'See more'}</Text>
                  <SvgIcon name="arrow-right" size={appTheme.sizes.iconSizeSm} color={themeColors.primary} />
                </View>
              </Clickable2>
            </View>
          ) : null}
        </Animated.View>
      </View>
    </Animated.View>
  );
  if (visualOnly) {
    return (
      <Animated.View
        style={[root_style, root_anim_style]}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants">
        {body}
      </Animated.View>
    );
  }
  return (
    <Clickable2 onPress={onPress} accessibilityRole="button" accessibilityState={{ selected: isActive }} haptics>
      {body}
    </Clickable2>
  );
});

export function CategorySlider({
  items,
  getImageUri,
  getTitle,
  selectedId,
  onSelect,
  onShowBanner,
  styles,
  themeColors,
  compact,
  collapseProgress,
}: {
  items: Array<{ id: string; [key: string]: any }>;
  getImageUri: (item: any) => string | null;
  getTitle: (item: any) => string;
  selectedId: string | number | null;
  onSelect: (id: string | number | null) => void;
  onShowBanner?: (item: any) => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  /** Custom-plan builder only — half-height slides for more form space. */
  compact?: boolean;
  /**
   * Merged list only — 0 = full category slide, 1 = custom-plan-sized (image only, full width).
   * Height-only animation (no scale). Driven on the UI thread.
   */
  collapseProgress?: SharedValue<number>;
}) {
  const { t } = useTranslation();
  /** Collapse mode lays out from expanded metrics and animates heights toward custom-plan metrics. */
  const metrics = useMemo(
    () => resolveSlideMetrics(collapseProgress != null ? false : compact),
    [compact, collapseProgress],
  );
  const active_index_from_id = useMemo(() => {
    const i = items.findIndex(it => String(it.id) === String(selectedId));
    return i >= 0 ? i : 0;
  }, [items, selectedId]);

  /** Local index so parent selection updates don’t yank the rail mid-gesture / mid-settle. */
  const [carousel_index, set_carousel_index] = useState(active_index_from_id);
  useEffect(() => {
    set_carousel_index(active_index_from_id);
  }, [active_index_from_id]);

  const image_uri_by_id = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const item of items) {
      out[String(item.id)] = getImageUri(item);
    }
    return out;
  }, [items, getImageUri]);

  useEffect(() => {
    const prefetch_indices = [carousel_index - 1, carousel_index, carousel_index + 1].filter(i => i >= 0 && i < items.length);
    for (const index of prefetch_indices) {
      const uri = image_uri_by_id[String(items[index]?.id)];
      if (uri) {
        Image.prefetch(uri).catch(() => {});
      }
    }
  }, [carousel_index, items, image_uri_by_id]);

  const on_active_index_change = useCallback(
    (index: number) => {
      set_carousel_index(index);
      const item = items[index];
      if (!item) {
        return;
      }
      if (String(item.id) === String(selectedId)) {
        return;
      }
      onSelect(item.id);
    },
    [items, onSelect, selectedId],
  );

  const can_show_see_more = useCallback(
    (item: (typeof items)[number]) =>
      Boolean(onShowBanner && (image_uri_by_id[String(item.id)] || getTitle(item))),
    [onShowBanner, image_uri_by_id, getTitle],
  );

  const card_height_sv = useDerivedValue(() => {
    if (collapseProgress == null) {
      return metrics.card_height;
    }
    return interpolate(
      collapseProgress.value,
      [0, 1],
      [EXPANDED_METRICS.card_height, COLLAPSED_METRICS.card_height],
      Extrapolation.CLAMP,
    );
  }, [collapseProgress, metrics.card_height]);

  const render_carousel_card = useCallback(
    (item: (typeof items)[number]) => {
      const is_active = String(selectedId) === String(item.id);
      return (
        <CategorySliderSlide
          imageUri={image_uri_by_id[String(item.id)] ?? null}
          title={getTitle(item)}
          isActive={is_active}
          onPress={() => onSelect(item.id)}
          show_see_more_visual={can_show_see_more(item)}
          visualOnly
          styles={styles}
          themeColors={themeColors}
          metrics={metrics}
          collapseProgress={collapseProgress}
        />
      );
    },
    [
      image_uri_by_id,
      getTitle,
      selectedId,
      onSelect,
      can_show_see_more,
      styles,
      themeColors,
      metrics,
      collapseProgress,
    ],
  );

  const overlay_footer_anim = useAnimatedStyle(() => {
    if (collapseProgress == null) {
      return { height: metrics.body_height, opacity: 1 };
    }
    const p = collapseProgress.value;
    return {
      height: interpolate(
        p,
        [0, 1],
        [EXPANDED_METRICS.body_height, COLLAPSED_METRICS.body_height],
        Extrapolation.CLAMP,
      ),
      opacity: interpolate(p, [0, 0.35, 1], [1, 0, 0], Extrapolation.CLAMP),
    };
  }, [collapseProgress, metrics.body_height]);

  const overlay_root_anim = useAnimatedStyle(() => {
    if (collapseProgress == null) {
      return { width: SLIDE_WIDTH, height: metrics.card_height };
    }
    return {
      width: SLIDE_WIDTH,
      height: interpolate(
        collapseProgress.value,
        [0, 1],
        [EXPANDED_METRICS.card_height, COLLAPSED_METRICS.card_height],
        Extrapolation.CLAMP,
      ),
    };
  }, [collapseProgress, metrics.card_height]);

  const render_scroll_overlay = useCallback(
    (item: (typeof items)[number]) => {
      if (!can_show_see_more(item)) {
        return <Animated.View style={overlay_root_anim} />;
      }
      return (
        <Animated.View style={overlay_root_anim} pointerEvents="box-none">
          <Animated.View
            style={[styles.categorySlideOverlayFooter, overlay_footer_anim]}
            pointerEvents="box-none">
            <Clickable2
              style={[styles.categorySlideSeeMore, styles.categorySlideOverlaySeeMore, styles.categorySlideSeeMoreHitTarget]}
              onPress={() => onShowBanner?.(item)}
              haptics
              hitSlop={appTheme.spacing.sm}
              accessibilityRole="button"
              accessibilityLabel={t('subscriptions.seeMore') || 'See more'}>
              <View style={styles.categorySlideSeeMoreHitArea} />
            </Clickable2>
          </Animated.View>
        </Animated.View>
      );
    },
    [can_show_see_more, onShowBanner, styles, t, overlay_footer_anim, overlay_root_anim],
  );

  const host_anim_style = useAnimatedStyle(() => {
    if (collapseProgress == null) {
      return {};
    }
    const p = collapseProgress.value;
    return {
      height: interpolate(
        p,
        [0, 1],
        [MERGED_CAROUSEL_EXPANDED_HEIGHT, MERGED_CAROUSEL_COLLAPSED_HEIGHT],
        Extrapolation.CLAMP,
      ),
      overflow: 'hidden' as const,
    };
  }, [collapseProgress]);

  if (items.length === 0) {
    return null;
  }

  const carousel = (
    <StackedCarousel
      data={items}
      keyExtractor={it => String(it.id)}
      renderCard={render_carousel_card}
      renderScrollOverlay={render_scroll_overlay}
      cardWidth={SLIDE_WIDTH}
      cardHeight={metrics.card_height}
      cardHeightSV={collapseProgress != null ? card_height_sv : undefined}
      stackOffset={SLIDE_STACK_OFFSET}
      activeIndex={carousel_index}
      onIndexChange={on_active_index_change}
      listStyle={styles.carouselTouchLayer}
      style={styles.stackedCarouselContainer}
      cardShellStyle={styles.stackedCarouselShell}
      paginatorRowStyle={styles.subscriptionCarouselPaginator}
      slidePaginatorGap={4}
      extraData={carousel_index}
      showPaginator
      paginatorVisibleDots={5}
      paginatorDotSize={7}
      paginatorSpacing={6}
    />
  );

  if (collapseProgress == null) {
    return <View style={styles.carouselHost}>{carousel}</View>;
  }

  return <Animated.View style={[styles.carouselHost, host_anim_style]}>{carousel}</Animated.View>;
}
